// SEQ-180 E20: keep `pnl_category_alias` in step when a P&L category is
// renamed in EditExpenseCategoriesModal. Plan section 6.2's rename path: when
// a category's display string changes, write a NEW alias row pointing the
// new string at the OLD string's metric_key, and leave the old row in place
// so historical claims_pnl_monthly_values rows (which still carry the old
// string) keep resolving. This module only handles the rename case; a
// genuinely new category with no prior alias needs a registry row too
// (plan section 6.1, "expense.custom.<hex>"), which is a database-owned
// change and out of scope here: it is left to resolve to expense.unmapped
// (plan decision D4), the same safety net an unrecognised string already
// gets today.

import type { SupabaseClient } from "@supabase/supabase-js";

export interface CategoryRename {
  oldName: string;
  newName: string;
}

export interface WriteRenameAliasesResult {
  /** Null on full success. Non-null names the first failure; earlier renames in the batch may have already been written. */
  error: string | null;
}

/**
 * Resolves each rename's existing metric_key via the `pnl_metric_key_for` RPC
 * (supabase/migrations/20260908000200_pnl_category_alias.sql) and upserts a
 * new `pnl_category_alias` row for the new name. Follows the X.1 rule: every
 * Supabase call's `{ error }` is checked explicitly, never left to a
 * `.catch()` that would never run.
 */
export async function writeRenameAliases(
  supabase: SupabaseClient,
  organizationId: string,
  renames: CategoryRename[],
): Promise<WriteRenameAliasesResult> {
  for (const { oldName, newName } of renames) {
    /* eslint-disable @typescript-eslint/no-explicit-any -- pnl_metric_key_for is not yet in generated Supabase types */
    const { data: metricKey, error: keyError } = await (supabase as any).rpc("pnl_metric_key_for", {
      p_org: organizationId,
      p_category: oldName,
    });
    /* eslint-enable @typescript-eslint/no-explicit-any */

    if (keyError) {
      return { error: `Could not resolve the existing category key for "${oldName}": ${keyError.message}` };
    }

    // No prior alias for this string: nothing to carry forward. The renamed
    // string falls back to expense.unmapped until an admin gives it its own
    // registry row, which is D4's intended behaviour, not a bug.
    if (!metricKey) continue;

    /* eslint-disable @typescript-eslint/no-explicit-any -- pnl_category_alias is not yet in generated Supabase types */
    const { error: upsertError } = await (supabase as any)
      .from("pnl_category_alias")
      .upsert(
        { organization_id: organizationId, category_text: newName, metric_key: metricKey },
        { onConflict: "organization_id,category_text" },
      );
    /* eslint-enable @typescript-eslint/no-explicit-any */

    if (upsertError) {
      return { error: `Could not save the analytics alias for "${newName}": ${upsertError.message}` };
    }
  }

  return { error: null };
}
